import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Loader } from "@/components/Common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Plus, Trash2, Printer, UtensilsCrossed } from "lucide-react";

const PALETTES = [
  { id: "ivory", name: "Ivoire", bg: "#F7EFE3", fg: "#4A3B2A", accent: "#CBA35C" },
  { id: "sage", name: "Sauge", bg: "#EEF2E9", fg: "#33402B", accent: "#657668" },
  { id: "noir", name: "Noir", bg: "#111110", fg: "#F4F0EA", accent: "#D4AF37" },
  { id: "blush", name: "Poudré", bg: "#FAF4F3", fg: "#5A4A52", accent: "#D9A89F" },
];

const DEFAULT_COURSES = [
  { name: "Apéritif", items: ["Bulles de bienvenue", "Canapés du terroir"] },
  { name: "Entrée", items: ["Truite du Léman, agrumes & aneth"] },
  { name: "Plat", items: ["Filet de bœuf, jus corsé, légumes de saison"] },
  { name: "Fromages", items: ["Sélection de fromages d'alpage"] },
  { name: "Dessert", items: ["Pièce montée & mignardises"] },
];

export default function MenuEditor() {
  const { wedding } = useAuth();
  const wid = wedding?.wedding?.id;
  const w = wedding?.wedding;
  const [design, setDesign] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    const { data } = await api.get(`/weddings/${wid}/invitations`);
    const existing = data.find((d) => d.type === "menu");
    if (existing) {
      existing.config = { palette: "ivory", courses: DEFAULT_COURSES, ...existing.config };
      setDesign(existing);
    } else {
      setDesign(null);
    }
    setLoading(false);
  };
  useEffect(() => { if (wid) load(); }, [wid]);

  const createMenu = async () => {
    const { data } = await api.post(`/weddings/${wid}/invitations`, {
      type: "menu", template: "menu", title: "Menu du dîner",
      config: { palette: "ivory", header: "Menu", subheader: w?.names || "", footer: "Bon appétit", courses: DEFAULT_COURSES },
    });
    data.config = { palette: "ivory", courses: DEFAULT_COURSES, ...data.config };
    setDesign(data);
  };

  const save = async (next) => {
    setDesign(next);
    await api.patch(`/weddings/${wid}/invitations/${next.id}`, { config: next.config });
  };
  const setCfg = (patch) => save({ ...design, config: { ...design.config, ...patch } });
  const setCourse = (i, patch) => {
    const courses = design.config.courses.map((c, x) => (x === i ? { ...c, ...patch } : c));
    setCfg({ courses });
  };
  const addCourse = () => setCfg({ courses: [...(design.config.courses || []), { name: "Nouveau service", items: [""] }] });
  const delCourse = (i) => setCfg({ courses: design.config.courses.filter((_, x) => x !== i) });
  const setItem = (ci, ii, v) => {
    const items = design.config.courses[ci].items.map((it, x) => (x === ii ? v : it));
    setCourse(ci, { items });
  };
  const addItem = (ci) => setCourse(ci, { items: [...design.config.courses[ci].items, ""] });
  const delItem = (ci, ii) => setCourse(ci, { items: design.config.courses[ci].items.filter((_, x) => x !== ii) });

  if (loading) return <Loader />;
  if (!design) return (
    <div className="card-lux p-10 text-center max-w-lg mx-auto">
      <div className="w-14 h-14 rounded-2xl bg-accent flex items-center justify-center mx-auto mb-4"><UtensilsCrossed className="w-6 h-6" /></div>
      <h3 className="font-serif text-2xl mb-2">Créez votre menu</h3>
      <p className="text-sm text-stone-500 mb-6">Composez le déroulé de votre repas, service par service, avec une carte élégante à imprimer.</p>
      <Button onClick={createMenu} className="rounded-full" data-testid="create-menu"><Plus className="w-4 h-4 mr-1" /> Créer mon menu</Button>
    </div>
  );

  const pal = PALETTES.find((p) => p.id === design.config.palette) || PALETTES[0];
  const FB = { bg: pal.bg, fg: pal.fg, title: pal.fg, accent: pal.accent };
  const C = (k) => (design.config.colors?.[k]) || FB[k];
  const style = design.config.style || "classic";
  const setColor = (k, v) => setCfg({ colors: { ...(design.config.colors || {}), [k]: v } });
  const align = style === "minimal" ? "text-left" : "text-center";
  const decoChar = { floral: "❀", romantique: "♡", botanique: "❦", chateau: "✦", artdeco: "◆" }[style] || "";

  return (
    <div className="grid lg:grid-cols-2 gap-6">
      <div className="card-lux p-5 space-y-3">
        <div className="flex items-center gap-2 mb-1"><UtensilsCrossed className="w-4 h-4" /><h3 className="font-serif text-lg">Composition du menu</h3></div>
        <div><Label>Titre</Label><Input value={design.config.header || ""} onChange={(e) => setCfg({ header: e.target.value })} className="mt-1 h-10" data-testid="menu-header" /></div>
        <div><Label>Sous-titre (noms / date)</Label><Input value={design.config.subheader || ""} onChange={(e) => setCfg({ subheader: e.target.value })} className="mt-1 h-10" /></div>
        <div><Label>Palette</Label>
          <div className="flex gap-2 mt-1.5">
            {PALETTES.map((p) => (
              <button key={p.id} onClick={() => setCfg({ palette: p.id })} data-testid={`menu-pal-${p.id}`}
                className={`w-9 h-9 rounded-full border-2 ${design.config.palette === p.id ? "ring-2 ring-champagne" : ""}`}
                style={{ background: p.bg, borderColor: p.accent }} title={p.name} />
            ))}
          </div></div>
        <div><Label>Couleurs personnalisées</Label>
          <div className="grid grid-cols-2 gap-2 mt-1.5">
            {[["bg", "Fond"], ["fg", "Texte"], ["title", "Titres"], ["accent", "Ornements"]].map(([k, l]) => (
              <div key={k} className="flex items-center gap-2">
                <input type="color" value={C(k)} onChange={(e) => setColor(k, e.target.value)} data-testid={`menu-color-${k}`} className="w-8 h-8 rounded border p-0.5 cursor-pointer" />
                <div className="flex-1"><div className="text-[10px] text-stone-400">{l}</div>
                  <Input value={C(k)} onChange={(e) => setColor(k, e.target.value)} className="h-7 text-xs font-mono" /></div>
              </div>
            ))}
          </div></div>
        <div><Label>Style</Label>
          <div className="flex gap-2 mt-1.5 flex-wrap">
            {[["minimal", "Minimal"], ["floral", "Floral"], ["blacktie", "Black Tie"], ["artdeco", "Art Déco"], ["romantique", "Romantique"], ["botanique", "Botanique"], ["chateau", "Château"]].map(([k, l]) => (
              <button key={k} onClick={() => setCfg({ style: k })} data-testid={`menu-style-${k}`} className={`text-xs px-3 py-1.5 rounded-full border ${style === k ? "bg-stone-900 text-white border-stone-900" : "bg-white"}`}>{l}</button>
            ))}
          </div></div>
        <div className="border-t pt-3 space-y-3">
          {(design.config.courses || []).map((c, ci) => (
            <div key={ci} className="border rounded-xl p-3" data-testid={`course-${ci}`}>
              <div className="flex items-center gap-2 mb-2">
                <Input value={c.name} onChange={(e) => setCourse(ci, { name: e.target.value })} className="h-9 font-medium" placeholder="Nom du service" />
                <button onClick={() => delCourse(ci)} className="text-stone-300 hover:text-destructive"><Trash2 className="w-4 h-4" /></button>
              </div>
              {c.items.map((it, ii) => (
                <div key={ii} className="flex items-center gap-2 mb-1.5">
                  <Input value={it} onChange={(e) => setItem(ci, ii, e.target.value)} className="h-8 text-sm" placeholder="Plat / description" />
                  <button onClick={() => delItem(ci, ii)} className="text-stone-300 hover:text-destructive"><Trash2 className="w-3.5 h-3.5" /></button>
                </div>
              ))}
              <button onClick={() => addItem(ci)} className="text-xs text-champagne mt-1">+ Ajouter un plat</button>
            </div>
          ))}
          <Button variant="outline" onClick={addCourse} className="w-full rounded-full" data-testid="add-course"><Plus className="w-4 h-4 mr-1" /> Ajouter un service</Button>
        </div>
        <div><Label>Pied de page</Label><Input value={design.config.footer || ""} onChange={(e) => setCfg({ footer: e.target.value })} className="mt-1 h-10" /></div>
        <Button variant="outline" className="w-full rounded-full" onClick={() => window.print()}><Printer className="w-4 h-4 mr-1" /> Imprimer / PDF</Button>
      </div>

      <div className="flex items-start justify-center">
        <div className="w-full max-w-sm aspect-[3/4] rounded-2xl shadow-xl overflow-hidden flex flex-col" style={{ background: C("bg"), color: C("fg") }} data-testid="menu-preview">
          {style === "artdeco" && <div className="h-2" style={{ background: C("accent") }} />}
          <div className="p-8 flex-1 overflow-y-auto"
            style={style === "blacktie" ? { border: `2px solid ${C("accent")}`, margin: 8 } : style === "chateau" ? { border: `1px solid ${C("accent")}`, outline: `3px solid ${C("accent")}`, outlineOffset: 3, margin: 10 } : {}}>
            <div className={align}>
              {["floral", "botanique", "romantique", "chateau", "artdeco"].includes(style) && decoChar &&
                <div className="text-lg mb-1" style={{ color: C("accent") }}>{decoChar}</div>}
              <div className={`font-serif ${style === "artdeco" || style === "blacktie" ? "uppercase tracking-[0.32em] text-2xl" : style === "romantique" ? "italic text-4xl" : "text-3xl"}`} style={{ color: C("title") }}>{design.config.header || "Menu"}</div>
              {design.config.subheader && <div className="text-xs uppercase tracking-[0.28em] mt-2" style={{ color: C("accent") }}>{design.config.subheader}</div>}
              {style !== "minimal" && (["floral", "romantique", "botanique"].includes(style)
                ? <div className="my-4 text-center" style={{ color: C("accent") }}>{decoChar || "•"} • {decoChar || "•"}</div>
                : <div className="w-12 h-px mx-auto my-4" style={{ background: C("accent") }} />)}
            </div>
            <div className="space-y-4">
              {(design.config.courses || []).map((c, ci) => (
                <div key={ci} className={align} style={style === "botanique" ? { borderLeft: `2px solid ${C("accent")}`, paddingLeft: 10 } : {}}>
                  <div className={`text-[11px] mb-1 ${style === "romantique" ? "italic" : "uppercase tracking-[0.3em]"}`} style={{ color: C("accent") }}>{c.name}</div>
                  {c.items.filter(Boolean).map((it, ii) => <div key={ii} className="font-serif text-sm leading-snug">{it}</div>)}
                </div>
              ))}
            </div>
            {design.config.footer && <div className={`${align} text-xs italic mt-4 opacity-70`}>{design.config.footer}</div>}
          </div>
          {style === "artdeco" && <div className="h-2" style={{ background: C("accent") }} />}
        </div>
      </div>
    </div>
  );
}
